import { useState, useMemo, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  LinearProgress,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

// Context and Components
import { useAuth } from '../contexts/AuthContext';
import { QUICK_ACTIONS } from '../constants/dashboard';
import { TableSkeleton, StatCardSkeleton, ContentCardSkeleton, PageHeaderCard, EmptyState } from '../components/common';

// Services
import { dashboardAPI, projectsAPI, activitiesAPI } from '../services/api';

// Utils and Constants
import {
  getProjectStatusColor,
  getProjectStatusLabel,
  formatDateMMDDYYYY,
  getActivityVisualConfig,
  formatRelativeTime,
} from '../utils/helper';

const DASHBOARD_SORT_FIELD_MAP = {
  name: 'name',
  manager: 'manager_name',
  status: 'status',
  dueDate: 'due_date',
};

const Dashboard = () => {
  const { user, getActiveCompany } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const activeCompany = useMemo(() => getActiveCompany(), [getActiveCompany]);
  const activeCompanyId = activeCompany?.id || null;

  // All hooks must be called before any early return
  const [isLoading, setIsLoading] = useState(true);
  const [recentProjects, setRecentProjects] = useState([]);
  const [recentProjectsError, setRecentProjectsError] = useState('');
  const [isRecentProjectsLoading, setIsRecentProjectsLoading] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ field: null, direction: 'asc' });
  const [showActivitySearch, setShowActivitySearch] = useState(false);
  const [activitySearchTerm, setActivitySearchTerm] = useState('');
  const [debouncedActivitySearchTerm, setDebouncedActivitySearchTerm] = useState('');
  const [recentActivities, setRecentActivities] = useState([]);
  const [recentActivitiesError, setRecentActivitiesError] = useState('');
  const [isRecentActivitiesLoading, setIsRecentActivitiesLoading] = useState(false);
  const [summaryCounts, setSummaryCounts] = useState({
    totalProjects: 0,
    activeTasks: 0,
    pendingReports: 0,
    teamMembers: 0,
  });

  // Simulate loading (will be replaced with real API call)
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 350);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const fetchDashboardSummary = async () => {
      if (!activeCompanyId) {
        setSummaryCounts({
          totalProjects: 0,
          activeTasks: 0,
          pendingReports: 0,
          teamMembers: 0,
        });
        return;
      }

      try {
        const summary = await dashboardAPI.getSummary(activeCompanyId);
        setSummaryCounts({
          totalProjects: summary.totalProjects || 0,
          activeTasks: summary.activeTasks || 0,
          pendingReports: summary.pendingReports || 0,
          teamMembers: summary.teamMembers || 0,
        });
      } catch {
        setSummaryCounts({
          totalProjects: 0,
          activeTasks: 0,
          pendingReports: 0,
          teamMembers: 0,
        });
      }
    };

    fetchDashboardSummary();
  }, [activeCompanyId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedActivitySearchTerm(activitySearchTerm.trim());
    }, 350);

    return () => clearTimeout(timer);
  }, [activitySearchTerm]);

  useEffect(() => {
    const fetchRecentActivities = async () => {
      if (!activeCompanyId) {
        setRecentActivities([]);
        setRecentActivitiesError('');
        return;
      }

      setIsRecentActivitiesLoading(true);
      setRecentActivitiesError('');

      try {
        const activities = await activitiesAPI.getActivitiesByCompany(activeCompanyId, {
          search: debouncedActivitySearchTerm || undefined,
          page: 1,
          pageSize: 5,
          sortBy: 'created_at',
          sortOrder: 'desc',
        });

        const mapped = (Array.isArray(activities) ? activities : []).map((activity) => {
          const visuals = getActivityVisualConfig(activity.action, activity.entityType, activity.metadata);
          return {
            ...activity,
            id: activity.id,
            title: activity.title,
            user: activity.actor?.name || 'System',
            time: formatRelativeTime(activity.createdAt),
            icon: visuals.icon,
            color: visuals.color,
            type: visuals.type,
          };
        });

        setRecentActivities(mapped);
      } catch (error) {
        setRecentActivities([]);
        setRecentActivitiesError(error.message || 'Failed to fetch recent activities');
      } finally {
        setIsRecentActivitiesLoading(false);
      }
    };

    fetchRecentActivities();
  }, [activeCompanyId, debouncedActivitySearchTerm]);

  useEffect(() => {
    const fetchRecentProjects = async () => {
      if (!activeCompanyId) {
        setRecentProjects([]);
        setRecentProjectsError('');
        return;
      }

      setIsRecentProjectsLoading(true);
      setRecentProjectsError('');

      try {
        const sortBy = DASHBOARD_SORT_FIELD_MAP[sortConfig.field] || 'updated_at';
        const projects = await projectsAPI.listByCompany(activeCompanyId, {
          search: debouncedSearchTerm || undefined,
          sortBy,
          sortOrder: sortConfig.direction,
          page: 1,
          pageSize: 5,
        });

        const projectsWithProgress = projects.map((project) => ({
          ...project,
          dueDate: project.dueDate || project.due_date || '',
          manager: project.managerName || project.manager_name || 'Unassigned',
          progress: project.progress ?? 0,
          taskCount: project.taskCount ?? project.task_count ?? 0,
        }));

        setRecentProjects(projectsWithProgress);
      } catch (error) {
        setRecentProjects([]);
        setRecentProjectsError(error.message || 'Failed to fetch recent projects');
      } finally {
        setIsRecentProjectsLoading(false);
      }
    };

    fetchRecentProjects();
  }, [activeCompanyId, debouncedSearchTerm, sortConfig.field, sortConfig.direction]);

  // Filter and sort projects - must be before early return
  const filteredAndSortedProjects = useMemo(() => {
    let result = [...recentProjects];

    if (sortConfig.field === 'progress') {
      result.sort((a, b) => {
        const aVal = a.progress;
        const bVal = b.progress;
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [recentProjects, sortConfig]);

  // Check if Century 21 is selected - redirect to Real Estate Dashboard
  if (activeCompany?.id === 'century21') {
    return <Navigate to="/real-estate" replace />;
  }

  // Handle sort
  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleQuickAction = (actionId) => {
    if (actionId === 'new-project') {
      navigate('/projects/create');
      return;
    }

    if (actionId === 'new-task') {
      navigate('/projects');
      return;
    }

    if (actionId === 'create-report') {
      navigate('/reports/weekly');
    }
  };

  const summaryData = [
    {
      id: 'projects',
      title: 'Total Projects',
      value: summaryCounts.totalProjects,
      icon: FolderIcon,
      color: '#1976d2',
      trend: 'up',
      trendValue: '--',
    },
    {
      id: 'tasks',
      title: 'Active Tasks',
      value: summaryCounts.activeTasks,
      icon: AssignmentIcon,
      color: '#2e7d32',
      trend: 'up',
      trendValue: '--',
    },
    {
      id: 'reports',
      title: 'Pending Reports',
      value: summaryCounts.pendingReports,
      icon: DescriptionIcon,
      color: '#ed6c02',
      trend: 'up',
      trendValue: '--',
    },
    {
      id: 'team',
      title: 'Team Members',
      value: summaryCounts.teamMembers,
      icon: GroupIcon,
      color: '#9c27b0',
      trend: 'up',
      trendValue: '--',
    },
  ];

  return (
    <Box>
      <PageHeaderCard
        title={`Welcome back, ${user?.name || 'User'}`}
        subtitle={`Here's what's happening with your projects today.`}
        titleVariant={isMobile ? 'h5' : 'h4'}
        titleFontWeight={700}
        mb={{ xs: 2, sm: 4 }}
      />

      {isLoading ? (
        <StatCardSkeleton count={4} />
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: { xs: 1.5, sm: 2 },
          mt: { xs: 0.5, sm: 1 }
        }}>
          {summaryData.map((item) => {
            const IconComponent = item.icon;
            const TrendIcon = item.trend === 'up' ? TrendingUpIcon : TrendingDownIcon;
            return (
              <Card
                key={item.id}
                elevation={0}
                sx={{
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  cursor: 'pointer',
                  height: { xs: 100, sm: 120 },
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, height: '100%', display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: { xs: 1, sm: 2 }, width: '100%' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, mb: 0.5, fontWeight: 500 }}>
                        {item.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, lineHeight: 1.2 }}>
                          {item.value}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <TrendIcon sx={{
                            fontSize: { xs: 14, sm: 16 },
                            color: item.trend === 'up' ? 'success.main' : 'error.main'
                          }} />
                          <Typography
                            variant="caption"
                            sx={{
                              color: item.trend === 'up' ? 'success.main' : 'error.main',
                              fontWeight: 600,
                              fontSize: { xs: '0.65rem', sm: '0.75rem' }
                            }}
                          >
                            {item.trendValue}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        p: { xs: 1, sm: 1.5 },
                        borderRadius: 2,
                        backgroundColor: `${item.color}12`,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <IconComponent sx={{ color: item.color, fontSize: { xs: 24, sm: 28 } }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Quick Actions */}
      <Box sx={{ mt: { xs: 3, sm: 4 } }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
          Quick Actions
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, flexWrap: 'wrap' }}>
          {QUICK_ACTIONS.map((action) => {
            const IconComponent = action.icon;
            return (
              <Button
                key={action.id}
                variant="outlined"
                size="small"
                startIcon={<IconComponent sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                onClick={() => handleQuickAction(action.id)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                  px: { xs: 1.5, sm: 2 },
                  py: 0.5,
                  borderRadius: 1.5,
                  borderColor: 'primary.main',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.50',
                  },
                }}
              >
                {action.label}
              </Button>
            );
          })}
        </Box>
      </Box>

      {/* Recent Projects and Recent Activity */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 2, sm: 3 },
        mt: { xs: 3, sm: 4 }
      }}>
        {/* Recent Projects */}
        <Box sx={{ flex: 2, minWidth: 0 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              height: '100%',
              backgroundColor: 'background.paper',
              boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
              border: '1px solid',
              borderColor: 'grey.100',
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 1.5, sm: 2 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 8,
                    height: 24,
                    borderRadius: 1,
                    backgroundColor: 'primary.main',
                  }} />
                  <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                    Recent Projects
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => setShowSearch(!showSearch)}
                    sx={{
                      color: showSearch ? 'primary.main' : 'text.secondary',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  >
                    <SearchIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                  <Button
                    size="small"
                    endIcon={<ArrowForwardIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
                    onClick={() => navigate('/projects')}
                    sx={{
                      textTransform: 'none',
                      fontSize: { xs: '0.75rem', sm: '0.8rem' },
                      fontWeight: 500,
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  >
                    View All
                  </Button>
                </Box>
              </Box>
              {/* Collapsible Search */}
              <Collapse in={showSearch}>
                <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
                  <TextField
                    size="small"
                    placeholder="Search projects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" sx={{ fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchTerm('')}>
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ maxWidth: 400 }}
                  />
                </Box>
              </Collapse>
              {recentProjectsError && (
                <Box sx={{ px: { xs: 2, sm: 3 }, pb: 1 }}>
                  <Typography variant="caption" color="error.main">
                    {recentProjectsError}
                  </Typography>
                </Box>
              )}
              {isLoading || isRecentProjectsLoading ? (
                <Box sx={{ p: 2 }}>
                  <TableSkeleton columns={isMobile ? 3 : 5} rows={5} />
                </Box>
              ) : (
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size={isMobile ? 'small' : 'medium'}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200', minWidth: 140 }}>
                          <TableSortLabel
                            active={sortConfig.field === 'name'}
                            direction={sortConfig.field === 'name' ? sortConfig.direction : 'asc'}
                            onClick={() => handleSort('name')}
                            sx={{
                              '& .MuiTableSortLabel-icon': {
                                opacity: sortConfig.field === 'name' ? 1 : 0.3,
                              },
                              '&:hover .MuiTableSortLabel-icon': {
                                opacity: 1,
                              },
                            }}
                          >
                            Project
                          </TableSortLabel>
                        </TableCell>
                        {!isMobile && (
                          <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>
                            <TableSortLabel
                              active={sortConfig.field === 'manager'}
                              direction={sortConfig.field === 'manager' ? sortConfig.direction : 'asc'}
                              onClick={() => handleSort('manager')}
                              sx={{
                                '& .MuiTableSortLabel-icon': {
                                  opacity: sortConfig.field === 'manager' ? 1 : 0.3,
                                },
                                '&:hover .MuiTableSortLabel-icon': {
                                  opacity: 1,
                                },
                              }}
                            >
                              Manager
                            </TableSortLabel>
                          </TableCell>
                        )}
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>
                          <TableSortLabel
                            active={sortConfig.field === 'status'}
                            direction={sortConfig.field === 'status' ? sortConfig.direction : 'asc'}
                            onClick={() => handleSort('status')}
                            sx={{
                              '& .MuiTableSortLabel-icon': {
                                opacity: sortConfig.field === 'status' ? 1 : 0.3,
                              },
                              '&:hover .MuiTableSortLabel-icon': {
                                opacity: 1,
                              },
                            }}
                          >
                            Status
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200', minWidth: 120 }}>
                          <TableSortLabel
                            active={sortConfig.field === 'progress'}
                            direction={sortConfig.field === 'progress' ? sortConfig.direction : 'asc'}
                            onClick={() => handleSort('progress')}
                            sx={{
                              '& .MuiTableSortLabel-icon': {
                                opacity: sortConfig.field === 'progress' ? 1 : 0.3,
                              },
                              '&:hover .MuiTableSortLabel-icon': {
                                opacity: 1,
                              },
                            }}
                          >
                            Progress
                          </TableSortLabel>
                        </TableCell>
                        {!isMobile && (
                          <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>
                            <TableSortLabel
                              active={sortConfig.field === 'dueDate'}
                              direction={sortConfig.field === 'dueDate' ? sortConfig.direction : 'asc'}
                              onClick={() => handleSort('dueDate')}
                              sx={{
                                '& .MuiTableSortLabel-icon': {
                                  opacity: sortConfig.field === 'dueDate' ? 1 : 0.3,
                                },
                                '&:hover .MuiTableSortLabel-icon': {
                                  opacity: 1,
                                },
                              }}
                            >
                              Due Date
                            </TableSortLabel>
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAndSortedProjects.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={isMobile ? 3 : 5} sx={{ border: 0 }}>
                            <EmptyState
                              variant={searchTerm ? 'search' : 'projects'}
                              actionLabel={!searchTerm ? 'Create Project' : undefined}
                              onAction={!searchTerm ? () => navigate('/projects/create') : undefined}
                            />
                          </TableCell>
                        </TableRow>
                      ) : filteredAndSortedProjects.map((project, index) => (
                        <TableRow
                          key={project.id}
                          onClick={() => navigate(`/projects/${project.id}`)}
                          sx={{
                            backgroundColor: index % 2 === 0 ? 'transparent' : 'grey.50',
                            cursor: 'pointer',
                            transition: 'background-color 0.15s ease',
                            '&:hover': {
                              backgroundColor: 'primary.50',
                            },
                          }}
                        >
                          <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                            <Typography variant="body2" fontWeight={500} noWrap sx={{ fontSize: '0.85rem' }}>
                              {project.name}
                            </Typography>
                          </TableCell>
                          {!isMobile && (
                            <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                {project.manager}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                            <Chip
                              label={getProjectStatusLabel(project.status)}
                              color={getProjectStatusColor(project.status)}
                              size="small"
                              sx={{
                                fontWeight: 500,
                                fontSize: '0.7rem',
                                height: 24,
                                '& .MuiChip-label': { px: 1.5 }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 120, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={project.progress}
                                sx={{
                                  flex: 1,
                                  height: 6,
                                  borderRadius: 3,
                                  backgroundColor: 'grey.200',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 3,
                                    backgroundColor: project.progress === 100 ? 'success.main' : 'primary.main',
                                  },
                                }}
                              />
                              <Typography variant="caption" fontWeight={500} color="text.secondary" sx={{ minWidth: 35, fontSize: '0.75rem' }}>
                                {project.progress}%
                              </Typography>
                            </Box>
                          </TableCell>
                          {!isMobile && (
                            <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                {project.dueDate ? formatDateMMDDYYYY(project.dueDate) : '-'}
                              </Typography>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Recent Activity */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              height: '100%',
              backgroundColor: 'background.paper',
              boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
              border: '1px solid',
              borderColor: 'grey.100',
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 8,
                    height: 24,
                    borderRadius: 1,
                    backgroundColor: 'secondary.main',
                  }} />
                  <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                    Recent Activity
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => setShowActivitySearch(!showActivitySearch)}
                    sx={{
                      color: showActivitySearch ? 'primary.main' : 'text.secondary',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  >
                    <SearchIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                  <Button
                    size="small"
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                    onClick={() => navigate('/activities')}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  >
                    View All
                  </Button>
                </Box>
              </Box>
              {/* Collapsible Search */}
              <Collapse in={showActivitySearch}>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    placeholder="Search activities..."
                    value={activitySearchTerm}
                    onChange={(e) => setActivitySearchTerm(e.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" sx={{ fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: activitySearchTerm && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setActivitySearchTerm('')}>
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Collapse>
              {recentActivitiesError && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="error.main">
                    {recentActivitiesError}
                  </Typography>
                </Box>
              )}
              {isLoading || isRecentActivitiesLoading ? (
                <ContentCardSkeleton lines={5} />
              ) : (
                recentActivities.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No activities found</Typography>
                  </Box>
                ) : (
                  <Box sx={{ position: 'relative' }}>
                    {/* Timeline line */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: { xs: 15, sm: 17 },
                        top: 20,
                        bottom: 20,
                        width: 2,
                        backgroundColor: 'grey.200',
                        borderRadius: 1,
                      }}
                    />
                    {recentActivities.map((activity) => {
                      const IconComponent = activity.icon;
                      return (
                        <Box
                          key={activity.id}
                          onClick={() => navigate(`/activities/${activity.id}`)}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                            py: 1.5,
                            px: 1,
                            position: 'relative',
                            pl: 0,
                            transition: 'all 0.15s ease',
                            borderRadius: 1,
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'grey.50',
                            },
                          }}
                        >
                          {/* Timeline dot */}
                          <Box
                            sx={{
                              width: { xs: 32, sm: 36 },
                              height: { xs: 32, sm: 36 },
                              borderRadius: '50%',
                              backgroundColor: 'background.paper',
                              border: '3px solid',
                              borderColor: activity.color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              zIndex: 1,
                            }}
                          >
                            <IconComponent sx={{ color: activity.color, fontSize: { xs: 14, sm: 16 } }} />
                          </Box>
                          <Box sx={{ flex: 1, pt: 0.5 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem', lineHeight: 1.4, mb: 0.25 }}>
                              {activity.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              {activity.user} - {activity.time}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
